import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  GlobeAltIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  LinkIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  FeatureGrid,
  GlassCard,
  PremiumCTA,
  inputClass,
  btnPrimaryClass,
} from '../premium/PremiumPageUI';
import { submitFormEmail } from '../../lib/submitFormEmail';

const networkTypes = [
  {
    title: 'Technology Professionals',
    description: 'Connect with engineers, architects, and specialists across cloud, security, and software domains.',
    icon: UserGroupIcon,
    tags: ['Mentorship', 'Events', 'Referrals'],
  },
  {
    title: 'Enterprise Leaders',
    description: 'Join CIOs and IT directors sharing insights on digital transformation and vendor strategy.',
    icon: BuildingOffice2Icon,
    tags: ['Roundtables', 'Briefings', 'Partners'],
  },
  {
    title: 'Global Alliances',
    description: 'Access our extended partner ecosystem spanning vendors, integrators, and certified resellers.',
    icon: GlobeAltIcon,
    tags: ['Fortinet', 'Cisco', 'Cloud'],
  },
];

const perks = [
  'Exclusive webinars and tech briefings',
  'Early access to product launches and pilots',
  'Co-marketing and referral opportunities',
  'Priority support channel for network members',
  'Annual summit and regional meetups',
  'Shared knowledge base and best practices',
];

export default function JoinNetwork() {
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', interest: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitFormEmail({
        formName: 'Network Application',
        fields: {
          fullName: form.name,
          email: form.email,
          company: form.company,
          role: form.role,
          interest: form.interest,
        },
      });
      alert('Thank you! We will review your application and reach out shortly.');
      setForm({ name: '', email: '', company: '', role: '', interest: '' });
    } catch (error) {
      alert(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      <PremiumHero
        badge="Professional Network"
        title="Join Our"
        highlight="Innovation Network"
        subtitle="Connect with technology leaders, enterprise innovators, and strategic partners in a curated ecosystem built for collaboration and growth."
        variant="network"
        stats={[
          { value: '200+', label: 'Members' },
          { value: '40+', label: 'Enterprises' },
          { value: '12', label: 'Events / Year' },
          { value: 'Global', label: 'Reach' },
        ]}
        actions={[
          { label: 'Apply to Join', primary: true, onClick: () => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }) },
          { label: 'View Partnerships', href: '/partnerships' },
        ]}
      />

      <PremiumSection>
        <SectionHeader
          badge="Membership"
          title="Who It's"
          highlight="For"
          subtitle="Whether you're an individual expert or an enterprise leader—there's a place in our network."
        />
        <FeatureGrid items={networkTypes} columns={3} />
      </PremiumSection>

      <PremiumSection alt>
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeader
              badge="Member Benefits"
              title="Network"
              highlight="Perks"
              className="text-left mb-8"
            />
            <ul className="space-y-4">
              {perks.map((perk) => (
                <li key={perk} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600 dark:text-gray-300 text-sm">{perk}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-3 mt-8 text-sm font-semibold text-[#2563EB]">
              <LinkIcon className="w-5 h-5" />
              <span>Curated · Verified · Enterprise-focused</span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <GlassCard className="p-8 md:p-10" id="apply">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB] to-cyan-500 flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Membership Application</h3>
                  <p className="text-sm text-slate-500 dark:text-gray-400">We'll respond within 3 business days</p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Full Name</label>
                    <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Email</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Company</label>
                    <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Role</label>
                    <input type="text" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Why join?</label>
                  <textarea rows={4} required value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className={inputClass} />
                </div>
                <button type="submit" disabled={isSubmitting} className={`${btnPrimaryClass} disabled:opacity-60`}>
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </GlassCard>
          </div>
        </div>
      </PremiumSection>

      <PremiumCTA
        title="Expand Your Professional Circle"
        subtitle="Join a network of innovators shaping the future of technology in Nepal and beyond."
        primary={{ label: 'Explore Partnerships', href: '/partnerships' }}
        secondary={{ label: 'Contact Us', href: '/contact' }}
      />
    </PageShell>
  );
}
